// List view: event cards built from the shared result set. Uses textContent
// for all source-derived strings (no innerHTML injection). Clicking a card
// focuses its marker on the map.

import { categoryMeta } from '../lib/categories';
import { formatDateTime, formatDistance, formatPrice } from '../lib/format';
import { navigate } from '../lib/router';
import { eventDistanceMiles, store, visibleEvents, type AppState } from '../lib/state';
import type { NormalizedEvent } from '../types/event';

const SOURCE_LABELS: Record<string, string> = {
  local: 'View event',
  ticketmaster: 'Ticketmaster',
  seatgeek: 'SeatGeek',
  ics: 'Community feed',
  predicthq: 'PredictHQ',
  google: 'Web (Google)',
};

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function buildCard(event: NormalizedEvent, state: AppState): HTMLElement {
  const card = el('article', 'card');
  card.dataset.id = event.id;
  card.tabIndex = 0;
  card.setAttribute('role', 'button');
  const opensHint = event.source === 'local' ? 'Opens event details.' : 'Opens event page in a new tab.';
  card.setAttribute('aria-label', `${event.title}. ${formatDateTime(event.start)}. ${opensHint}`);

  if (event.imageUrl) {
    const img = el('img', 'card-img');
    img.src = event.imageUrl;
    img.alt = ''; // decorative; the title carries the meaning
    img.loading = 'lazy';
    img.decoding = 'async';
    // Source images vary wildly in resolution (Google/SerpApi often returns
    // ~80-200px thumbnails). Stretched across a full-width card they look badly
    // pixelated, so once the real pixels are known, drop anything too small to
    // render crisply (and drop broken images). A card with no image is clean.
    img.addEventListener('load', () => {
      if (img.naturalWidth < 320 || img.naturalHeight < 150) {
        img.remove();
        card.classList.add('no-img');
      }
    });
    img.addEventListener('error', () => {
      img.remove();
      card.classList.add('no-img');
    });
    card.appendChild(img);
  }

  const body = el('div', 'card-body');

  const meta = categoryMeta(event.category);
  const tag = el('span', 'card-cat', `${meta.emoji} ${meta.label}`);
  tag.style.setProperty('--chip', meta.color);
  body.appendChild(tag);

  body.appendChild(el('h3', 'card-title', event.title));
  body.appendChild(el('p', 'card-when', formatDateTime(event.start)));

  const venueLine = [event.venueName, event.city].filter(Boolean).join(', ');
  if (venueLine) body.appendChild(el('p', 'card-venue', venueLine));

  const dist = eventDistanceMiles(event, state);
  if (Number.isFinite(dist)) {
    body.appendChild(el('p', 'card-distance', `📍 ${formatDistance(dist)}`));
  }

  const price = formatPrice(event.priceMin, event.priceMax);
  if (price) body.appendChild(el('p', 'card-price', price));

  card.appendChild(body);

  // Clicking the card opens the event at its source. Local events route to the
  // in-app detail page; external events open the source link in a new tab. The
  // source name itself is not shown.
  const open = () => {
    const url = event.sourceUrl;
    if (!url) return;
    if (event.source === 'local') {
      try {
        navigate(new URL(url).pathname);
      } catch {
        navigate(url);
      }
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };
  card.addEventListener('click', open);
  card.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  });

  return card;
}

// Shimmer placeholders shown while a search is in flight, so the layout does
// not collapse to a single line of text and the wait feels shorter.
function buildSkeletonGrid(): HTMLElement {
  const wrap = el('div');
  const grid = el('div', 'card-grid');
  for (let i = 0; i < 6; i++) {
    const card = el('article', 'card skeleton');
    card.setAttribute('aria-hidden', 'true');
    card.appendChild(el('div', 'sk sk-img'));
    const b = el('div', 'card-body');
    b.append(
      el('div', 'sk sk-line sk-cat'),
      el('div', 'sk sk-line sk-title'),
      el('div', 'sk sk-line sk-sub'),
      el('div', 'sk sk-line sk-sub short')
    );
    card.appendChild(b);
    grid.appendChild(card);
  }
  const status = el('p', 'result-count', 'Finding events near you...');
  status.setAttribute('role', 'status');
  wrap.append(status, grid);
  return wrap;
}

// Toolbar above the results: live count plus a Soonest / Nearest sort toggle.
// The sort toggle only appears once we know the user's location.
function buildResultsBar(state: AppState, count: number): HTMLElement {
  const bar = el('div', 'results-bar');
  bar.appendChild(
    el(
      'p',
      'result-count',
      `${count} upcoming ${count === 1 ? 'event' : 'events'}` +
        (state.selectedDay ? ' on the selected day' : '')
    )
  );

  if (state.location) {
    const sort = el('div', 'sort-toggle');
    sort.setAttribute('role', 'group');
    sort.setAttribute('aria-label', 'Sort events');
    const opts: Array<{ key: 'soonest' | 'nearest'; label: string }> = [
      { key: 'soonest', label: 'Soonest' },
      { key: 'nearest', label: 'Nearest' },
    ];
    for (const o of opts) {
      const btn = el('button', 'sort-btn', o.label) as HTMLButtonElement;
      btn.type = 'button';
      const on = state.sortBy === o.key;
      btn.classList.toggle('on', on);
      btn.setAttribute('aria-pressed', String(on));
      btn.addEventListener('click', () => store.setSort(o.key));
      sort.appendChild(btn);
    }
    bar.appendChild(sort);
  }
  return bar;
}

export function createListView(root: HTMLElement): void {
  function render(state: AppState): void {
    root.innerHTML = '';

    if (state.status === 'loading') {
      root.appendChild(buildSkeletonGrid());
      return;
    }
    if (state.status === 'error') {
      root.appendChild(
        el('div', 'placeholder error', state.error ?? 'Something went wrong.')
      );
      return;
    }
    if (state.status === 'idle') {
      root.appendChild(
        el('div', 'placeholder', 'Search by zip code or "Near me" to discover events.')
      );
      return;
    }

    // Partial-source-failure banner.
    const failed = (state.meta?.sources ?? []).filter((s) => s.configured && !s.ok);
    if (failed.length > 0) {
      const names = failed.map((s) => SOURCE_LABELS[s.source] ?? s.source).join(', ');
      root.appendChild(
        el(
          'div',
          'banner warn',
          `Some sources had trouble (${names}). Showing results from the others.`
        )
      );
    }
    const demo = (state.meta?.sources ?? []).find((s) =>
      s.error?.includes('demo fixtures')
    );
    if (demo) {
      root.appendChild(
        el(
          'div',
          'banner info',
          'No event source keys configured. Showing demo data. See the README to add keys.'
        )
      );
    }

    const events = visibleEvents(state);
    if (events.length === 0) {
      root.appendChild(
        el('div', 'placeholder', 'No upcoming events match. Try a wider radius or fewer filters.')
      );
      return;
    }

    root.appendChild(buildResultsBar(state, events.length));

    const grid = el('div', 'card-grid');
    for (const event of events) {
      const card = buildCard(event, state);
      if (event.id === state.selectedEventId) card.classList.add('selected');
      grid.appendChild(card);
    }
    root.appendChild(grid);
  }

  store.subscribe(render);
  render(store.get());
}
